// src/pages/VoterSimple.tsx
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useCampaignId } from "../hooks/useCampaignId";

type Row = {
  voter_id: string;
  first_name: string | null;
  middle_name: string | null;
  last_name: string | null;
  address1: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
};

type SearchMode = "starts" | "contains";
type SortCol = "last_name" | "first_name" | "address1" | "city" | "zip" | null;
type SortDir = "asc" | "desc";

export default function VoterSimple() {
  const campaignId = useCampaignId();

  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Keyset cursor (unsorted)
  type Cursor = { after?: string };
  const [pages, setPages] = useState<Cursor[]>([{ after: undefined }]);
  const keysetCursor = pages[pages.length - 1];

  // Offset paging (sorted)
  const [pageIdx, setPageIdx] = useState(0); // 0-based

  // Inputs
  const [mode, setMode] = useState<SearchMode>("starts");
  const [nameInput, setNameInput] = useState("");
  const [streetInput, setStreetInput] = useState("");

  // Sorting
  const [sortCol, setSortCol] = useState<SortCol>(null);
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  // Page size
  const [pageSize, setPageSize] = useState<number>(50);
  const PAGE_SIZE_OPTIONS = [50, 100];

  // Totals
  const [resultCount, setResultCount] = useState<number | null>(null);

  // debounce keystrokes
  const debounceName = useRef<number | null>(null);
  const debounceStreet = useRef<number | null>(null);

  // Normalize inputs
  const upperName = useMemo(() => nameInput.trim().toUpperCase(), [nameInput]);
  const upperStreet = useMemo(
    () => streetInput.trim().toUpperCase().replace(/[.,]/g, " "),
    [streetInput]
  );

  // Name tokenization
  const nameTokens = useMemo(() => {
    if (!upperName) return [] as string[];
    if (upperName.includes(",")) {
      const [left, right] = upperName.split(",", 2);
      const lastTok = left.trim();
      const other = right
        .split(" ")
        .map((t) => t.trim())
        .filter(Boolean);
      return [...other, lastTok]; // [...first/middle..., last]
    }
    return upperName
      .split(" ")
      .map((t) => t.trim())
      .filter(Boolean);
  }, [upperName]);

  // Street tokenization + synonyms
  const STREET_SYNONYMS: Record<string, string[]> = {
    ST: ["ST", "STREET"],
    STREET: ["ST", "STREET"],
    DR: ["DR", "DRIVE"],
    DRIVE: ["DR", "DRIVE"],
    RD: ["RD", "ROAD"],
    ROAD: ["RD", "ROAD"],
    AVE: ["AVE", "AVENUE"],
    AVENUE: ["AVE", "AVENUE"],
    BLVD: ["BLVD", "BOULEVARD"],
    BOULEVARD: ["BLVD", "BOULEVARD"],
    LN: ["LN", "LANE"],
    LANE: ["LN", "LANE"],
    CIR: ["CIR", "CIRCLE"],
    CIRCLE: ["CIR", "CIRCLE"],
    PKWY: ["PKWY", "PARKWAY"],
    PARKWAY: ["PKWY", "PARKWAY"],
    TER: ["TER", "TERRACE"],
    TERRACE: ["TER", "TERRACE"],
    TRL: ["TRL", "TRAIL"],
    TRAIL: ["TRL", "TRAIL"],
    PL: ["PL", "PLACE"],
    PLACE: ["PL", "PLACE"],
    CT: ["CT", "COURT"],
    COURT: ["CT", "COURT"],
    HWY: ["HWY", "HIGHWAY"],
    HIGHWAY: ["HWY", "HIGHWAY"],
  };

  const streetTokensAll = useMemo(() => {
    if (!upperStreet) return [] as string[];
    return upperStreet
      .split(/\s+/)
      .map((t) => t.trim())
      .filter(Boolean);
  }, [upperStreet]);

  const streetMeaningful = useMemo(
    () => streetTokensAll.filter((t) => t.length >= 3 || STREET_SYNONYMS[t]),
    [streetTokensAll]
  );

  // Validation for "contains"
  const validForQuery = useMemo(() => {
    if (mode === "starts") return true;
    if (nameTokens.length > 0 && nameTokens.some((t) => t.length < 3)) return false;
    if (streetTokensAll.length > 0 && streetMeaningful.length === 0) return false;
    return true;
  }, [mode, nameTokens, streetTokensAll, streetMeaningful]);

  const hint = useMemo(() => {
    if (mode === "starts") return "";
    if (nameTokens.length > 0 && nameTokens.some((t) => t.length < 3)) {
      return "For Contains: each name word must have at least 3 characters.";
    }
    if (streetTokensAll.length > 0 && streetMeaningful.length === 0) {
      return "For Contains: include at least one meaningful street word (e.g., PALM, COURT).";
    }
    return "";
  }, [mode, nameTokens, streetTokensAll, streetMeaningful]);

  const hasAnyFilter = useMemo(
    () => nameTokens.length > 0 || streetTokensAll.length > 0,
    [nameTokens.length, streetTokensAll.length]
  );

  // Reset pagination when inputs change
  useEffect(() => {
    if (!campaignId) return;
    setRows([]);
    setErr(null);
    if (sortCol === null) {
      setPages([{ after: undefined }]); // keyset reset
    } else {
      setPageIdx(0); // offset reset
    }
  }, [campaignId, mode, upperName, upperStreet, sortCol, sortDir, pageSize]);

  // Helpers to apply filters
  function applyNameFilters<T extends any>(q: T) {
    const pat = (t: string) => (mode === "starts" ? `${t}%` : `%${t}%`);
    if (nameTokens.length === 0) return q;

    if (nameTokens.length === 1) {
      const t = nameTokens[0];
      // @ts-ignore
      q = q.or(
        [
          `last_name.ilike.${pat(t)}`,
          `first_name.ilike.${pat(t)}`,
          `middle_name.ilike.${pat(t)}`,
        ].join(",")
      );
    } else {
      const lastTok = nameTokens[nameTokens.length - 1];
      const others = nameTokens.slice(0, -1);
      // @ts-ignore
      q = q.ilike("last_name", pat(lastTok));
      for (const t of others) {
        // @ts-ignore
        q = q.or([`first_name.ilike.${pat(t)}`, `middle_name.ilike.${pat(t)}`].join(","));
      }
    }
    return q;
  }

  function applyStreetFilters<T extends any>(q: T) {
    if (streetTokensAll.length === 0) return q;

    const startsPat = (t: string) => `${t}%`;
    const containsPat = (t: string) => `%${t}%`;

    function orSynonyms(qx: any, token: string, forStarts: boolean) {
      const syns = STREET_SYNONYMS[token];
      if (!syns) {
        return qx.ilike("address1", forStarts ? startsPat(token) : containsPat(token));
      }
      const orGroup = syns
        .map((s) => `address1.ilike.${forStarts ? startsPat(s) : containsPat(s)}`)
        .join(",");
      return qx.or(orGroup);
    }

    if (mode === "starts") {
      const [first, ...rest] = streetTokensAll;
      // @ts-ignore
      q = STREET_SYNONYMS[first] ? orSynonyms(q, first, true) : q.ilike("address1", startsPat(first));
      for (const t of rest) {
        // @ts-ignore
        q = orSynonyms(q, t, false);
      }
    } else {
      for (const t of streetMeaningful) {
        // @ts-ignore
        q = orSynonyms(q, t, false);
      }
    }
    return q;
  }

  // Totals (HEAD count)
  useEffect(() => {
    if (!campaignId) return;
    if (!validForQuery) {
      setResultCount(null);
      return;
    }
    (async () => {
      try {
        let qc = supabase
          .from("campaign_voters_with_address")
          .select("voter_id", { count: "exact", head: true })
          .eq("campaign_id", campaignId);

        qc = applyNameFilters(qc);
        qc = applyStreetFilters(qc);

        const { count, error } = (await qc) as any;
        if (error) throw error;
        setResultCount(typeof count === "number" ? count : null);
      } catch (e: any) {
        console.error("count error:", e?.message ?? e);
        setResultCount(null);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, mode, nameTokens, streetTokensAll, streetMeaningful, validForQuery]);

  // Data fetching
  useEffect(() => {
    if (!campaignId) return;
    if (!validForQuery) {
      setRows([]);
      setErr(null);
      return;
    }

    (async () => {
      try {
        setLoading(true);
        setErr(null);

        if (sortCol === null) {
          // UNSORTED → KEYSET
          let qIds = supabase
            .from("campaign_voters_with_address")
            .select("voter_id")
            .eq("campaign_id", campaignId)
            .order("voter_id", { ascending: true })
            .limit(pageSize);

          if (keysetCursor.after) qIds = qIds.gt("voter_id", keysetCursor.after);

          qIds = applyNameFilters(qIds);
          qIds = applyStreetFilters(qIds);

          const { data: idRows, error: idErr } = await qIds;
          if (idErr) throw idErr;

          const ids = (idRows ?? []).map((r: any) => r.voter_id as string);
          if (ids.length === 0) {
            setRows([]);
            setLoading(false);
            return;
          }

          let qView = supabase
            .from("campaign_voters_with_address")
            .select("voter_id, first_name, middle_name, last_name, address1, city, state, zip")
            .eq("campaign_id", campaignId)
            .in("voter_id", ids)
            .order("voter_id", { ascending: true });

          qView = applyNameFilters(qView);
          qView = applyStreetFilters(qView);

          const { data, error } = await qView;
          if (error) throw error;

          const orderMap = new Map(ids.map((id, i) => [id, i]));
          const sorted = (data ?? []).slice().sort((a: any, b: any) => {
            return (orderMap.get(a.voter_id) ?? 0) - (orderMap.get(b.voter_id) ?? 0);
          }) as Row[];

          setRows(sorted);
        } else {
          // SORTED → OFFSET
          const from = pageIdx * pageSize;
          const to = from + pageSize - 1;

          let q = supabase
            .from("campaign_voters_with_address")
            .select("voter_id, first_name, middle_name, last_name, address1, city, state, zip")
            .eq("campaign_id", campaignId);

          q = applyNameFilters(q);
          q = applyStreetFilters(q);

          if (sortCol === "address1") {
            q = q
              .order("address1", { ascending: sortDir === "asc" })
              .order("city", { ascending: true })
              .order("zip", { ascending: true })
              .order("voter_id", { ascending: true });
          } else {
            q = q.order(sortCol, { ascending: sortDir === "asc" }).order("voter_id", { ascending: true });
          }

          q = q.range(from, to);

          const { data, error } = await q;
          if (error) throw error;
          setRows((data ?? []) as Row[]);
        }
      } catch (e: any) {
        setErr(e?.message ?? String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [
    campaignId,
    mode,
    nameTokens,
    streetTokensAll,
    streetMeaningful,
    validForQuery,
    keysetCursor.after,
    pageIdx,
    pageSize,
    sortCol,
    sortDir,
  ]);

  // Paging
  function nextPage() {
    if (sortCol === null) {
      if (rows.length === 0) return;
      const lastId = rows[rows.length - 1]?.voter_id;
      if (!lastId) return;
      setPages((p) => [...p, { after: lastId }]);
    } else {
      setPageIdx((p) => p + 1);
    }
  }
  function prevPage() {
    if (sortCol === null) {
      setPages((p) => (p.length > 1 ? p.slice(0, -1) : p));
    } else {
      setPageIdx((p) => Math.max(0, p - 1));
    }
  }

  // Sorting
  function toggleSort(col: Exclude<SortCol, null>) {
    if (sortCol !== col) {
      setSortCol(col);
      setSortDir("asc");
      setPageIdx(0);
    } else {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      setPageIdx(0);
    }
  }
  function clearSort() {
    setSortCol(null);
    setSortDir("asc");
    setPages([{ after: undefined }]);
  }

  // Summary text
  const summaryText = useMemo(() => {
    const c = resultCount ?? "—";
    if (!hasAnyFilter) {
      return `Total voters: ${c} · Page size: ${pageSize}`;
    }
    if (sortCol === null) {
      return `Matches: ${c} · Page size: ${pageSize}`;
    } else {
      const start = pageIdx * pageSize + 1;
      const end = start + rows.length - 1;
      return `Matches: ${c} · Showing ${rows.length ? `${start}–${end}` : "0"} (sorted by ${sortCol} ${sortDir})`;
    }
  }, [resultCount, hasAnyFilter, sortCol, sortDir, pageIdx, pageSize, rows.length]);

  // UI
  return (
    <div style={{ padding: 16 }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>Voters (Simple)</h1>

      {!campaignId && <div style={{ marginBottom: 12, color: "#555" }}>Select a campaign from the top-right.</div>}

      {err && (
        <div
          style={{
            background: "#ffe8e8",
            border: "1px solid #f2b0b0",
            padding: 8,
            borderRadius: 6,
            marginBottom: 10,
            color: "#a40000",
            whiteSpace: "pre-wrap",
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
            fontSize: 12,
          }}
        >
          {err}
        </div>
      )}

      {/* Controls row */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr auto auto auto",
          gap: 10,
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        {/* Name input */}
        <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ width: 120 }}>Name</span>
          <input
            value={nameInput}
            onChange={(e) => {
              const v = e.target.value.toUpperCase();
              if (debounceName.current) window.clearTimeout(debounceName.current);
              debounceName.current = window.setTimeout(() => setNameInput(v), 250);
            }}
            placeholder="e.g. GARCIA | GENARO GARZA | GARZA, GENARO"
            style={{
              padding: 8,
              border: "1px solid #ddd",
              borderRadius: 8,
              textTransform: "uppercase",
              width: "100%",
            }}
          />
        </label>

        {/* Street input */}
        <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ width: 120 }}>Street</span>
          <input
            value={streetInput}
            onChange={(e) => {
              const v = e.target.value.toUpperCase();
              if (debounceStreet.current) window.clearTimeout(debounceStreet.current);
              debounceStreet.current = window.setTimeout(() => setStreetInput(v), 250);
            }}
            placeholder="e.g. S PALM COURT | PALM CT | PALM COURT DR"
            style={{
              padding: 8,
              border: "1px solid #ddd",
              borderRadius: 8,
              textTransform: "uppercase",
              width: "100%",
            }}
          />
        </label>

        {/* Mode toggle */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            border: "1px solid #ddd",
            padding: "6px 10px",
            borderRadius: 8,
            background: "#fff",
            height: 40,
          }}
        >
          <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <input type="radio" name="mode" value="starts" checked={mode === "starts"} onChange={() => setMode("starts")} />
            <span>Starts with</span>
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <input
              type="radio"
              name="mode"
              value="contains"
              checked={mode === "contains"}
              onChange={() => setMode("contains")}
            />
            <span>Contains</span>
          </label>
        </div>

        {/* Page size */}
        <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span>Per page</span>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
            style={{ padding: "6px 10px", border: "1px solid #ddd", borderRadius: 8, background: "#fff" }}
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>

        {/* Clear + sort reset */}
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={() => {
              setNameInput("");
              setStreetInput("");
            }}
            style={{ padding: "6px 10px", border: "1px solid #ddd", borderRadius: 6, background: "#fff" }}
          >
            Clear filters
          </button>
          {sortCol !== null && (
            <button
              onClick={clearSort}
              style={{ padding: "6px 10px", border: "1px solid #ddd", borderRadius: 6, background: "#fff" }}
            >
              Clear sort
            </button>
          )}
        </div>

        {/* Hints */}
        {mode === "contains" && hint && (
          <div style={{ gridColumn: "1 / -1", color: "#777", fontSize: 12 }}>{hint}</div>
        )}
      </div>

      {/* Summary line */}
      <div style={{ marginBottom: 8, color: "#555", fontSize: 13 }}>{summaryText}</div>

      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            {/* Voter ID column now always visible and clickable */}
            <Th>Voter ID</Th>

            <Th clickable onClick={() => toggleSort("last_name")} active={sortCol === "last_name"} dir={sortDir}>
              Last {sortCol === "last_name" ? (sortDir === "asc" ? "▲" : "▼") : ""}
            </Th>

            <Th clickable onClick={() => toggleSort("first_name")} active={sortCol === "first_name"} dir={sortDir}>
              First {sortCol === "first_name" ? (sortDir === "asc" ? "▲" : "▼") : ""}
            </Th>

            <Th>Middle</Th>

            <Th clickable onClick={() => toggleSort("address1")} active={sortCol === "address1"} dir={sortDir}>
              Address {sortCol === "address1" ? (sortDir === "asc" ? "▲" : "▼") : ""}
            </Th>

            <Th clickable onClick={() => toggleSort("city")} active={sortCol === "city"} dir={sortDir}>
              City {sortCol === "city" ? (sortDir === "asc" ? "▲" : "▼") : ""}
            </Th>

            <Th>State</Th>

            <Th clickable onClick={() => toggleSort("zip")} active={sortCol === "zip"} dir={sortDir}>
              ZIP {sortCol === "zip" ? (sortDir === "asc" ? "▲" : "▼") : ""}
            </Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.voter_id}>
              {/* clickable link to Voter Detail page, with campaign id query param */}
              <Td>
                <Link to={`/voters/${r.voter_id}?campaign=${campaignId}`} style={{ color: "#1e40af", textDecoration: "none" }}>
                  {r.voter_id}
                </Link>
              </Td>
              <Td>{r.last_name ?? ""}</Td>
              <Td>{r.first_name ?? ""}</Td>
              <Td>{r.middle_name ?? ""}</Td>
              <Td>{r.address1 ?? ""}</Td>
              <Td>{r.city ?? ""}</Td>
              <Td>{r.state ?? ""}</Td>
              <Td>{r.zip ?? ""}</Td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={8} style={{ padding: 8, color: "#777" }}>
                {loading ? "Loading…" : "No results"}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div style={{ marginTop: 12, display: "flex", gap: 8, alignItems: "center" }}>
        <button
          disabled={(sortCol === null ? pages.length <= 1 : pageIdx <= 0) || loading}
          onClick={prevPage}
          style={{
            padding: "6px 10px",
            borderRadius: 6,
            border: "1px solid #ddd",
            background:
              sortCol === null
                ? pages.length > 1
                  ? "#fff"
                  : "#f3f4f6"
                : pageIdx > 0
                ? "#fff"
                : "#f3f4f6",
          }}
        >
          Prev
        </button>
        <button
          disabled={rows.length < pageSize || loading}
          onClick={nextPage}
          style={{
            padding: "6px 10px",
            borderRadius: 6,
            border: "1px solid #ddd",
            background: rows.length === pageSize ? "#fff" : "#f3f4f6",
          }}
        >
          Next
        </button>
        {loading && <span style={{ color: "#555" }}>Loading…</span>}
      </div>
    </div>
  );
}

/** Small header/td helpers */
function Th(props: any) {
  const { children, clickable = false, active = false, dir = "asc", ...rest } = props;
  return (
    <th
      style={{
        textAlign: "left",
        borderBottom: "1px solid #ddd",
        padding: 8,
        cursor: clickable ? "pointer" : "default",
        userSelect: "none",
        background: active ? "#fafafa" : undefined,
        whiteSpace: "nowrap",
      }}
      {...rest}
    >
      {children}
    </th>
  );
}
function Td(props: any) {
  return <td style={{ padding: 8, borderBottom: "1px solid #eee" }} {...props} />;
}
